import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { historyAPI, progressAPI, achievementsAPI, questionsAPI } from '../services/api';

export default function QuizEnhanced() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const category = searchParams.get('category');
  const difficulty = searchParams.get('difficulty');
  const mode = searchParams.get('mode') || 'practice';

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [examTimeLeft, setExamTimeLeft] = useState(1800); // 30 minutes for exam mode
  const [examTimerActive, setExamTimerActive] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    loadQuestions();
  }, []);

  useEffect(() => {
    if (mode === 'timed' && timerActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (mode === 'timed' && timeLeft === 0 && !isAnswered) {
      // Time's up - auto submit as wrong
      handleTimeout();
    }
  }, [timeLeft, timerActive, isAnswered]);

  // Exam mode: 30-minute overall timer
  useEffect(() => {
    if (mode === 'exam' && examTimerActive && examTimeLeft > 0 && !quizComplete) {
      const timer = setTimeout(() => setExamTimeLeft(examTimeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (mode === 'exam' && examTimeLeft === 0 && !quizComplete) {
      // Time's up - auto submit exam
      handleExamTimeout();
    }
  }, [examTimeLeft, examTimerActive, quizComplete]);

  const loadQuestions = async () => {
    try {
      setLoadError(null);
      let response;

      if (mode === 'exam') {
        // Exam mode: 20 random questions from all categories
        response = await questionsAPI.getExamQuestions(20);
      } else if (mode === 'review') {
        // Review mode: Load random questions (TODO: Fetch actual wrong answers)
        response = await questionsAPI.getRandomQuestions(10);
      } else {
        // Practice/Timed mode: Load from selected category and difficulty
        response = await questionsAPI.getQuestions(category, difficulty, 10);
      }

      setQuestions(response.data);

      if (mode === 'timed') {
        setTimerActive(true);
      }
      if (mode === 'exam') {
        setExamTimerActive(true);
      }
    } catch (error) {
      console.error('Failed to load questions:', error);
      setLoadError(error.response?.data?.error || 'Failed to load questions. Please try again.');
    }
  };

  const handleAnswerSelect = (index) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) return;

    setIsAnswered(true);
    setTimerActive(false);

    const currentQuestion = questions[currentIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct;

    if (isCorrect) {
      setScore(score + 1);
      setStreak(streak + 1);
    } else {
      setStreak(0);
      setWrongAnswers([...wrongAnswers, {
        question: currentQuestion,
        userAnswer: selectedAnswer
      }]);
    }

    // Save to backend
    try {
      await historyAPI.recordAnswer({
        questionText: currentQuestion.q,
        category: category || 'mixed',
        difficulty: currentQuestion.difficulty,
        wasCorrect: isCorrect,
        userAnswer: currentQuestion.options[selectedAnswer],
        correctAnswer: currentQuestion.options[currentQuestion.correct]
      });
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleTimeout = () => {
    setIsAnswered(true);
    setTimerActive(false);
    setStreak(0);

    const currentQuestion = questions[currentIndex];
    setWrongAnswers([...wrongAnswers, {
      question: currentQuestion,
      userAnswer: null, // timeout
      timedOut: true
    }]);
  };

  const handleExamTimeout = () => {
    // Exam time's up - auto-submit with current progress
    setExamTimerActive(false);
    setQuizComplete(true);

    // Mark any unanswered questions as wrong
    const unansweredCount = questions.length - currentIndex - (isAnswered ? 1 : 0);
    if (unansweredCount > 0 && !isAnswered) {
      // Current question not answered
      const currentQuestion = questions[currentIndex];
      setWrongAnswers([...wrongAnswers, {
        question: currentQuestion,
        userAnswer: null,
        timedOut: true
      }]);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      if (mode === 'timed') {
        setTimeLeft(30);
        setTimerActive(true);
      }
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setQuizComplete(true);

    // Save progress to backend
    try {
      await progressAPI.updateProgress({
        score,
        questionsAnswered: questions.length,
        streak
      });

      // Check for achievements
      if (score >= 100) {
        await achievementsAPI.addAchievement({ name: 'Century', description: 'Score 100+ points' });
      }
      if (streak >= 10) {
        await achievementsAPI.addAchievement({ name: 'On Fire', description: '10 correct in a row' });
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Questions</h2>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <div className="space-x-4">
            <button onClick={() => loadQuestions()} className="btn btn-primary">
              Try Again
            </button>
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-white text-2xl">Loading questions...</div>
      </div>
    );
  }

  if (quizComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = mode === 'exam' ? score >= 15 : percentage >= 60;

    let stars = 0;
    if (percentage >= 90) stars = 3;
    else if (percentage >= 75) stars = 2;
    else if (percentage >= 60) stars = 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-8 text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Quiz Complete!</h1>

            {/* Stars */}
            <div className="text-6xl mb-6">
              {[...Array(stars)].map((_, i) => <span key={i}>⭐</span>)}
            </div>

            {/* Score */}
            <div className="text-6xl font-bold text-purple-600 mb-4">
              {score}/{questions.length}
            </div>
            <div className="text-2xl text-gray-600 mb-6">{percentage}%</div>

            {/* Result Message */}
            {mode === 'exam' && (
              <div className={`text-2xl font-bold mb-6 ${passed ? 'text-green-600' : 'text-red-600'}`}>
                {passed ? '✅ PASSED!' : '❌ Failed - Need 15/20 to pass'}
              </div>
            )}

            {/* Time's Up Message for Exam Mode */}
            {mode === 'exam' && examTimeLeft === 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⏰</span>
                  <div className="text-left">
                    <p className="font-bold text-yellow-800">Time Expired!</p>
                    <p className="text-sm text-yellow-700">The 30-minute exam time limit was reached. Your exam was auto-submitted.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Message */}
            <div className="text-lg text-gray-700 mb-8">
              {percentage >= 90 && "Outstanding! You're a Canadian citizenship expert!"}
              {percentage >= 75 && percentage < 90 && "Great job! You're well prepared!"}
              {percentage >= 60 && percentage < 75 && "Good effort! Keep studying!"}
              {percentage < 60 && "Keep studying! Review the material and try again!"}
            </div>

            {/* Wrong Answers Review */}
            {wrongAnswers.length > 0 && (
              <div className="text-left mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Questions to Review:</h3>
                <div className="space-y-4">
                  {wrongAnswers.map((wa, idx) => (
                    <div key={idx} className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                      <div className="font-bold text-gray-800 mb-2">{wa.question.q}</div>
                      {wa.timedOut ? (
                        <div className="text-sm text-red-600 mb-2">⏰ Time's up!</div>
                      ) : (
                        <div className="text-sm text-red-600 mb-2">
                          Your answer: {wa.question.options[wa.userAnswer]}
                        </div>
                      )}
                      <div className="text-sm text-green-600 font-bold">
                        Correct: {wa.question.options[wa.question.correct]}
                      </div>
                      {wa.question.explanation && (
                        <div className="text-sm text-gray-600 mt-2 italic">
                          {wa.question.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-x-4">
              <button onClick={() => window.location.reload()} className="btn btn-primary">
                Try Again
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600">
              Question {currentIndex + 1} of {questions.length}
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🔥</span>
                <span className="font-bold text-gray-800">{streak}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl">⭐</span>
                <span className="font-bold text-gray-800">{score}</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Timer (for timed mode) */}
        {mode === 'timed' && (
          <div className="bg-white rounded-lg shadow-xl p-4 mb-6 text-center">
            <div className={`text-4xl font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-gray-800'}`}>
              ⏱️ {timeLeft}s
            </div>
          </div>
        )}

        {/* Timer (for exam mode - 30 minutes total) */}
        {mode === 'exam' && (
          <div className={`rounded-lg shadow-xl p-6 mb-6 text-center ${
            examTimeLeft <= 300 ? 'bg-red-50 border-2 border-red-500' : 'bg-white'
          }`}>
            <div className="text-sm text-gray-600 mb-2 font-semibold">
              {examTimeLeft <= 300 ? '⚠️ TIME RUNNING OUT!' : 'Time Remaining'}
            </div>
            <div className={`text-5xl font-bold ${
              examTimeLeft <= 300 ? 'text-red-600 animate-pulse' :
              examTimeLeft <= 600 ? 'text-yellow-600' : 'text-gray-800'
            }`}>
              ⏱️ {Math.floor(examTimeLeft / 60)}:{String(examTimeLeft % 60).padStart(2, '0')}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {examTimeLeft <= 300 ? 'Less than 5 minutes left!' :
               examTimeLeft <= 600 ? 'Less than 10 minutes left' : 'Real test conditions - 30 minutes total'}
            </div>
          </div>
        )}

        {/* Question Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="mb-2 text-sm text-gray-600">
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
              {currentQuestion.difficulty}
            </span>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {currentQuestion.q}
          </h2>

          {/* Answer Options */}
          <div className="space-y-3 mb-6">
            {currentQuestion.options.map((option, index) => {
              let buttonClass = 'w-full p-4 text-left border-2 rounded-lg transition-all ';

              if (isAnswered) {
                if (index === currentQuestion.correct) {
                  buttonClass += 'border-green-500 bg-green-50 text-green-800';
                } else if (index === selectedAnswer) {
                  buttonClass += 'border-red-500 bg-red-50 text-red-800';
                } else {
                  buttonClass += 'border-gray-200 text-gray-600';
                }
              } else {
                if (index === selectedAnswer) {
                  buttonClass += 'border-purple-500 bg-purple-50';
                } else {
                  buttonClass += 'border-gray-200 hover:border-purple-300';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={isAnswered}
                  className={buttonClass}
                >
                  {option}
                  {isAnswered && index === currentQuestion.correct && ' ✅'}
                  {isAnswered && index === selectedAnswer && index !== currentQuestion.correct && ' ❌'}
                </button>
              );
            })}
          </div>

          {/* Explanation (shown after answering) */}
          {isAnswered && currentQuestion.explanation && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
              <div className="font-bold text-blue-800 mb-2">Explanation:</div>
              <div className="text-blue-700">{currentQuestion.explanation}</div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4">
            {!isAnswered ? (
              <button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
                className="btn btn-primary flex-1"
              >
                Submit Answer
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="btn btn-primary flex-1"
              >
                {currentIndex < questions.length - 1 ? 'Next Question →' : 'Finish Quiz'}
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
            >
              Quit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
